import React, { useMemo, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useQueryParams, StringParam } from "use-query-params";

import {
  Container,
  EmptyState,
  Link,
  ScreenContainer,
  ScreenHeader,
  Input,
  Select,
  Button,
} from "../components/wombat-ui";
import { useHistory } from "react-router";
import { parse } from "@formatjs/icu-messageformat-parser";

import { DocumentIcon } from "../components/icons";

import useViewer from "../hooks/useViewer";
import useTranslations from "../hooks/useTranslations";
import useNotification from "../hooks/useNotification";
import { useDebounce } from "react-use";
import {
  GetViewerDocument,
  useInsertTranslation,
} from "../graphql/generated/definitions";

type MessageFormProps = {
  message: { key: string; value: string; label: string };
};

const MessageForm: React.FC<MessageFormProps> = React.memo(
  ({ message: { key, value: defaultValue, label } }) => {
    const [value, setValue] = useState(defaultValue);
    const { locale } = useIntl();
    const handleChange = useCallback(
      (e) => setValue(e.target.value),
      [setValue]
    );

    const t = useTranslations();
    const { notifySuccess, notifyError } = useNotification();
    const [insertTranslation] = useInsertTranslation({
      refetchQueries: [{ query: GetViewerDocument }],
    });

    useDebounce(
      async () => {
        if (value !== defaultValue) {
          try {
            // We try to parse the value to ensure the user doesn't
            // save invalid values that could crash the app
            parse(value);

            await insertTranslation({
              variables: {
                input: {
                  key,
                  value,
                  locale: locale.split("-")[0], // the backend uses short locale
                },
              },
            });
            notifySuccess({
              title: t(`translations.notification.success.title`) as string,
            });
          } catch (e) {
            notifyError({
              title: t(`translations.notification.error.title`) as string,
            });
          }
        }
      },
      1000,
      [defaultValue, value]
    );

    return (
      <Input id={key} label={label} value={value} onChange={handleChange} />
    );
  }
);

const EditTranslationsScreen: React.FC = () => {
  const { isCapableOfInsertingTranslations } = useViewer();
  const t = useTranslations();

  const { messages } = useIntl();

  const messagesByNamespace = useMemo(
    () =>
      Object.entries(messages).reduce((messagesByNamespace, [key, value]) => {
        const namespace = key.split(".")[0];

        if (!messagesByNamespace[namespace]) {
          messagesByNamespace[namespace] = [];
        }

        messagesByNamespace[namespace].push({
          key: key,
          value: value,
          label: key.replace(`${namespace}.`, ""),
        });

        return messagesByNamespace;
      }, {}),
    [messages]
  );

  const namespaceOptions = useMemo(
    () => Object.keys(messagesByNamespace).sort(),
    [messagesByNamespace]
  );

  const [{ namespace = namespaceOptions[0] }, setQuery] = useQueryParams({
    namespace: StringParam,
  });

  const handleChangeNamespace = useCallback(
    (namespace) => setQuery({ namespace }, "replaceIn"),
    [setQuery]
  );

  const history = useHistory();

  return (
    <ScreenContainer
      error={
        !isCapableOfInsertingTranslations ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link variant="button" to="/">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      <ScreenHeader
        icon={DocumentIcon}
        title={t("translations.editLink") as string}
        description={t("translations.editDescription") as string}
        actions={[
          <Button key="backLink" variant="subtle" onClick={history.goBack}>
            {t("translations.back")}
          </Button>,
        ]}
      />
      <Container>
        <Select
          id="namespaceSelect"
          defaultValue={namespace}
          getValue={(namespace) => namespace}
          renderItem={(namespace) => namespace}
          onChange={handleChangeNamespace}
          items={namespaceOptions}
          label={t("translations.namespacesSelectLabel") as string}
        />
        <div className="grid grid-cols-2 gap-4 py-8">
          {messagesByNamespace[namespace]?.map((message) => (
            <MessageForm message={message} key={message.key} />
          ))}
        </div>
      </Container>
    </ScreenContainer>
  );
};

export default React.memo(EditTranslationsScreen);
